/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import moment from 'moment';
import { DownloadIcon, SearchIcon } from '../../../assets/icons';
import { formatAmount, formatMiles, fromStorage, getUpdatedState } from '../../../utils';
import { Input, Render } from '../../../components';
import { ReportService } from '../../../services';

const ReportBillingRoute = () => {
  const { data, filters, onChange, downloadAll, downloadOne, reload, search, total } = useFetchTable();

  return (
    <main className="w-full">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex flex-1 flex-wrap items-end gap-3">
          <Input
            className={{ base: 'max-w-60' }}
            placeholder="Buscar nombre, CI o RIF"
            value={filters.search}
            onValueChange={v => onChange('search', v)}
            prepend={
              <img src={SearchIcon} className="w-4 h-4" alt="" />
            }
          />

          <Input
            className={{ base: 'max-w-44' }}
            type="date"
            label="Desde"
            value={filters.since}
            onValueChange={v => onChange('since', v)}
          />

          <Input
            className={{ base: 'max-w-44' }}
            type="date"
            label="Hasta"
            value={filters.until}
            onValueChange={v => onChange('until', v)}
          />

          <div className="flex gap-4">
            <button
              className="bg-red hover:bg-red-700 text-white px-6 py-2 rounded-full text-sm font-medium transition-colors"
              onClick={() => search()}
            >
              Filtrar
            </button>
            <button
              className="bg-black hover:bg-gray-800 text-white px-6 py-2 rounded-full text-sm font-medium transition-colors"
              onClick={() => reload()}
            >
              Limpiar
            </button>
          </div>
        </div>
      </div>

      <div className="text-white font-semibold bg-orange px-3 py-1 rounded-xl max-w-max">
        <p className="text-lg font-bold">Total Comisión Generada</p>
        <p className="text-2xl font-black">{ formatAmount(total, '$') }</p>
      </div>

      <div className="bg-white rounded-2xl shadow-md overflow-hidden mt-6">
        <div className="flex items-center justify-between py-4 px-3">
          <p className="text-2xl font-bold text-center">Historial de facturación por ruta</p>

          <button
            className="flex items-center gap-2 bg-black text-white px-4 py-1.5 rounded-lg font-semibold"
            onClick={() => toast.promise(downloadAll(), { loading: 'Descargando...', success: 'Reporte descargado con éxito', error: 'Error al descargar el reporte' })}
          >
            <img src={DownloadIcon} className="w-4 h-4 brightness-200" alt="" />
            Descargar
          </button>
        </div>

        <div className="overflow-x-auto px-4 pb-4">
          <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_6rem] gap-2 mb-4 text-[11px] [&>div]:text-sm items-end font-bold text-default whitespace-pre-line">
            <div>Ruta</div>
            <div>Fecha del viaje</div>
            <div>Monto Ingresos{'\n'}generados (Bs)</div>
            <div>Monto Ingresos{'\n'}generados ($)</div>
            <div>Comisión{'\n'}Generada (Bs)</div>
            <div>Comisión{'\n'}Generada ($)</div>
            <div className="text-center">Acción</div>
          </div>

          <div className="space-y-3">
            {data.map((item, index) => (
              <>
                <div
                  key={item.id}
                  className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_6rem] gap-y-2 items-center [&>div]:py-3 [&>div]:bg-[#F2F2F2] rounded-xl hover:shadow-md transition-shadow text-sm"
                >
                  <div className="!bg-transparent">
                    <Render condition={!index || item?.order?.route_id !== data.at(index - 1)?.order?.route_id}>
                      <span>{ item?.order?.route?.name }</span>
                    </Render>
                  </div>
                  <div className="rounded-l-lg -ml-4 pl-2">{ moment(item?.order?.date).format('DD/MM/YYYY') }</div>
                  <div>{ formatMiles(item?.in_local, true, 'Bs. ') }</div>
                  <div>{ formatMiles(item?.in, true, '$') }</div>
                  <div>{ formatMiles(item?.out_local, true, 'Bs. ') }</div>
                  <div>{ formatMiles(item?.out, true, '$') }</div>
                  <div className="flex items-center justify-center gap-3 rounded-r-lg">
                    <img
                      src={DownloadIcon}
                      className="w-5 h-5 object-contain cursor-pointer"
                      onClick={() => {
                        toast.promise(
                          downloadOne(item?.order?.route_id),
                          {
                            loading: 'Descargando...',
                            success: 'Reporte impreso con éxito',
                            error: 'Error al imprimir el reporte',
                          }
                        );
                      }}
                      alt=""
                    />
                  </div>
                </div>

                <Render condition={(data.length - 1) === index || item?.order?.route_id !== data.at(index + 1)?.order?.route_id}>
                  <TotalRow data={data} total={total} routeId={item?.order?.route_id} />
                </Render>
              </>
            ))}
          </div>

          {!data.length && (
            <p className="py-8 text-text text-center">Sin datos para mostrar</p>
          )}
        </div>
      </div>
    </main>
  );
};

const TotalRow = ({ data, routeId, total }) => {
  const totals = useMemo(() => {
    const totals = {
      out: 0,
      out_local: 0,
      percentage: 0,
    }

    data.filter(i => i.order?.route_id === routeId).forEach(i => {
      totals.out += Number(i.out);
      totals.out_local += Number(i.out_local);
    });

    totals.percentage = Number(totals.out) / Number(total) * 100;

    return totals;
  }, [data, routeId]);

  return (
    <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_6rem] items-center bg-[#242424] text-white font-semibold py-2 rounded-xl hover:shadow-md transition-shadow text-base">
      <div className="col-span-4 pl-2">Total Comisión Ruta</div>
      <div>{ formatMiles(totals?.out_local, true, 'Bs. ') }</div>
      <div>{ formatMiles(totals?.out, true, '$') }</div>
      <div className="text-right pr-2">
        <p>Porcentaje</p>
        <p>{ formatMiles(totals?.percentage) }%</p>
      </div>
    </div>
  )
}

const useFetchTable = () => {
  const initialFilters = {
    search: '',
    since: '',
    until: '',
  };

  const [data, setData] = useState([]);
  const [total, setTotals] = useState(0);
  const [canFetch, setCanFetch] = useState(true);
  const [filters, setFilters] = useState(initialFilters);

  const fetchData = async () => {
    if (!canFetch) return;
    setCanFetch(false);

    try {
      const payload = await getUpdatedState(setFilters);
      const response = await ReportService.admin.billing.findByRouteAll(payload);

      setData(response.data);
      setTotals(response.totals.out);
      setCanFetch(true);

    } catch (error) {
      setData([]);
      setCanFetch(true);
    }
  }

  const downloadAll = async () => {
    try {
      const response = await ReportService.admin.billing.downloadByRouteAll(filters);
      if (response?.url) {
        const link = document.createElement('a');
        link.href = fromStorage(response.url);
        link.target = '_blank';
        link.download = response.url.split('/').pop();
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

    } catch (error) {
      console.log(error);
    }
  }

  const downloadOne = async (id) => {
    try {
      const response = await ReportService.admin.billing.downloadByRouteOne(id);
      if (response?.url) {
        const link = document.createElement('a');
        link.href = fromStorage(response.url);
        link.target = '_blank';
        link.download = response.url.split('/').pop();
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.log(error);
    }
  }

  const reload = (inSamePage = false) => {
    setCanFetch(true);
    if (!inSamePage) setFilters(initialFilters);
    fetchData();
  }

  const onChange = (target, value) => {
    setFilters(s => ({ ...s, [target]: value }));
  }

  useEffect(() => {
    fetchData();
  }, []);

  return {
    data,
    downloadAll,
    downloadOne,
    filters,
    isLoading: !canFetch,
    onChange,
    reload,
    search: fetchData,
    total,
  }
}

export default ReportBillingRoute;